import { useContext, useEffect, useState } from 'react'

import { useTenant } from '@rewardops/auth'
import { useFilter } from '@rewardops/ui'

import { SortOptionsKeys } from '~/utils/filter-utils'
import { SearchTypes } from '~/utils/sort-utils'
import ResponsiveComponent from '~/components/responsive-component'
import { ResizeContext } from '~/context/resize-context'
import ProductsSearch from '../components/search/products/products-search'
import RetailersSearch from '../components/search/retailers/retailers-search'

const commonStyles = `
  fixed bottom-0 left-0 right-0 z-10 overflow-auto bg-white transition-all
  duration-300 ease-in-out will-change-opacity will-change-transform`

export function SearchOverlay() {
  const tenant = useTenant()
  const { clearAndOverrideFilter, searchQuery, searchType } = useFilter()
  const showRetailers =
    !!tenant?.flags?.retailers && searchType !== SearchTypes.Products

  const [isVisible, setIsVisible] = useState(false)
  const windowSize = useContext(ResizeContext)
  const [notificationRibbonElement, setNotificationRibbonElement] =
    useState<HTMLElement | null>(null)

  useEffect(() => {
    const notificationRibbon = document?.getElementById('notification-ribbon')

    if (notificationRibbon) {
      setNotificationRibbonElement(notificationRibbon)
    }
  }, [windowSize])

  useEffect(() => {
    setIsVisible(!!searchQuery && !!searchType)
  }, [searchQuery, searchType])

  useEffect(() => {
    if (!isVisible) return

    // Set default values when the search overlay is opened
    clearAndOverrideFilter({
      q: searchQuery,
      searchType: searchType,
      sort: showRetailers
        ? SortOptionsKeys.NameAsc
        : SortOptionsKeys.CreatedAt,
    })

    // Every time when search query is updated we need to reset the sort and filter options
  }, [isVisible, searchQuery, searchType])

  const desktopComponent = (
    <div
      className={`${commonStyles} ${
        isVisible
          ? 'pointer-events-auto opacity-100'
          : 'pointer-events-none opacity-0'
      } ${notificationRibbonElement ? 'top-[8rem]' : 'top-[5.05rem]'}`}
    >
      <div className="container mx-auto pb-10 lg:max-w-[1250px]">
        {showRetailers ? <RetailersSearch /> : <ProductsSearch />}
      </div>
    </div>
  )

  const mobileComponent = (
    <div
      className={`${commonStyles} ${
        isVisible
          ? 'pointer-events-auto translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-full opacity-0'
      }`}
      style={{
        top: `${
          notificationRibbonElement
            ? notificationRibbonElement?.offsetHeight + 40
            : 40
        }px`,
      }}
    >
      <div className="mx-auto pb-10">
        {showRetailers ? <RetailersSearch /> : <ProductsSearch />}
      </div>
    </div>
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}
